// Browser-safe (no zapatos, pg, logger, flags or core imports): the app and server-side saves share it.

import type { DerivedCampaignStatus } from "./types/derived-campaign-status";
import type { JourneyState } from "./types/journey-state";

/**
 * Where a save lands:
 * - `"publish"`: insert a content row and advance `published_version` to it;
 *   the dispatcher sends it immediately. Used on Draft / journey / standalone
 *   surfaces where there is no active send to stage against.
 * - `"stage"`: insert a content row and advance `draft_version` only, leaving
 *   `published_version` untouched until an explicit publish. Used on campaigns
 *   past Draft.
 */
export type ContentSaveMode = "publish" | "stage";

/**
 * Content editability + where a save lands. Discriminated union: `saveMode` is
 * present **only** when content is editable, so `{ canEdit: false, saveMode: … }`
 * cannot be constructed and a non-editable surface never computes a `saveMode`.
 */
export type ContentEditability =
  | { canEdit: false }
  | { canEdit: true; saveMode: ContentSaveMode };

export type SurfaceEditability = {
  /** Structural edits: name rename, the editable overview layout, and (via
   *  `isDraft`) the Draft-only drawers. Campaign: the publishable set. Journey:
   *  draft/resetting/staged. Standalone: always. */
  canEditStructure: boolean;
  /** Narrower than `canEditStructure`: strict Draft-equivalent. Gates the
   *  draft-gated drawers (audience/schedule/metadata) and the mount-time
   *  auto-detach persist. Campaign: strict Draft. Journey:
   *  draft/resetting/staged. Standalone: always. */
  isDraft: boolean;
  /** Message type, subscription topic, sender and reply-to. Campaign: Draft,
   *  or Paused, which has no run in flight. Journey and standalone: as `isDraft`. */
  canEditSettings: boolean;
  content: ContentEditability;
};

export type SurfaceInput =
  | { trigger: "campaign"; derivedStatus: DerivedCampaignStatus | undefined }
  | { trigger: "journey"; journeyState: JourneyState }
  | { trigger: "realtimeJourney" }
  | { trigger: "standalone" };

/**
 * Journey states where structural edits (name rename, the full editable overview
 * layout, the Draft-only drawers) are allowed. `staged` folds in here because
 * journey staging is incomplete; this mirrors the canvas, which already
 * treats `staged` as draft-like (`graph-context.tsx`).
 */
const STRUCTURAL_JOURNEY_STATES = new Set<JourneyState>([
  "draft",
  "resetting",
  "staged",
]);
export const isStructurallyEditableJourneyState = (s: JourneyState): boolean =>
  STRUCTURAL_JOURNEY_STATES.has(s);

/**
 * Journey states that are actively dispatching. Content edits on these must
 * **stage** against an explicit publish rather than going live immediately, so
 * an in-flight journey isn't force-published on every save. Every other state
 * (`draft`/`resetting`/`staged`, not yet sending, and `paused`/`off`/`deleting`,
 * not sending) publishes content immediately, matching the canvas behavior.
 */
const STAGING_JOURNEY_STATES = new Set<JourneyState>(["active", "draining"]);
export const getJourneyContentSaveMode = (s: JourneyState): ContentSaveMode =>
  STAGING_JOURNEY_STATES.has(s) ? "stage" : "publish";

export const isDraftStatus = (status?: DerivedCampaignStatus): boolean =>
  status != null && status === "draft";

/**
 * Campaign statuses where message content edits are allowed. Edits in
 * Scheduled/Running/Paused/Error **stage** as an unpublished draft (advance
 * `draft_version`); they go live only on an explicit Publish, when the
 * dispatcher's per-fragment claim picks up the new `published_version` for
 * not-yet-claimed fragments. Completed/Archived stay read-only (terminal).
 */
export const EDITABLE_CAMPAIGN_STATUSES = new Set<DerivedCampaignStatus>([
  "draft",
  "scheduled",
  "running",
  "paused",
  "error",
]);

export const isEditableCampaignStatus = (
  status: DerivedCampaignStatus | null | undefined,
): boolean => status != null && EDITABLE_CAMPAIGN_STATUSES.has(status);

/**
 * The single source of truth for message-editability policy. Pure function over
 * the surface's trigger + status; collapses the per-state matrix to one
 * verifiable place. Called by the app's `useCurrentSurface` (for the outlet-mounted
 * surfaces) and by the app's message-detail loader (for the header, which renders
 * outside the outlet).
 */
export function deriveSurfaceEditability(
  input: SurfaceInput,
): SurfaceEditability {
  switch (input.trigger) {
    case "campaign": {
      const canEditStructure = isEditableCampaignStatus(input.derivedStatus);
      const isDraft = isDraftStatus(input.derivedStatus);
      return {
        canEditStructure,
        isDraft,
        canEditSettings: isDraft || input.derivedStatus === "paused",
        content: canEditStructure
          ? { canEdit: true, saveMode: isDraft ? "publish" : "stage" }
          : { canEdit: false },
      };
    }
    case "journey": {
      const structural = isStructurallyEditableJourneyState(input.journeyState);
      return {
        canEditStructure: structural,
        isDraft: structural,
        canEditSettings: structural,
        content: {
          canEdit: true,
          saveMode: getJourneyContentSaveMode(input.journeyState),
        },
      };
    }
    // The journey canvas owns a realtime tile's message, since the journey save prepares its realtime sends.
    case "realtimeJourney":
      return {
        canEditStructure: false,
        isDraft: false,
        canEditSettings: false,
        content: { canEdit: false },
      };
    case "standalone":
      return {
        canEditStructure: true,
        isDraft: true,
        canEditSettings: true,
        content: { canEdit: true, saveMode: "publish" },
      };
  }
}
